package pl.nickers.match3

import kotlinx.serialization.Serializable

@Serializable
data class JourneyEntry(val id: Int, val name: String, val file: String)

@Serializable
data class LevelGoalConfig(val pieceId: String, val target: Int)

@Serializable
data class LevelConfig(
    val id: Int,
    val goals: List<LevelGoalConfig>,
    val iceCount: Int = 0,
    val bombUses: Int = 0,
    val rowBombUses: Int = 1,
    val targetedRemoveUses: Int = 0,
    val moveLimit: Int? = null,
    /** Identifier of the piece set this level uses. Defaults to the canonical "default" set. */
    val pieceSet: String = "default",
    /** Board width (columns). Null defaults to [GRID_SIZE] when the level is loaded. */
    val width: Int? = null,
    /** Board height (rows). Null defaults to [GRID_SIZE] when the level is loaded. */
    val height: Int? = null,
    /**
     * Optional 1–3 star score cutoffs `[s1, s2, s3]` (strictly increasing). A winning
     * score that meets-or-exceeds s1/s2/s3 earns 1/2/3 stars.
     *
     * Omit (null) to derive sensible defaults from the level — see
     * [effectiveStarThresholds]. Non-increasing or otherwise malformed values are
     * normalized to a strictly-increasing ladder by [normalizeStarThresholds]; raw
     * input is never used as-is when it would produce a broken ladder.
     */
    val starThresholds: List<Int>? = null,
) {
    /**
     * The thresholds used at runtime:
     *   - the raw [starThresholds] normalized to a strictly-increasing 3-element
     *     ladder, when supplied; otherwise
     *   - sensible defaults derived from this level's goals and move budget via
     *     [defaultStarThresholds].
     *
     * Always returns exactly three strictly-increasing cutoffs `[s1, s2, s3]`.
     */
    val effectiveStarThresholds: List<Int>
        get() = normalizeStarThresholds(starThresholds) ?: defaultStarThresholds()

    /**
     * Sensible default star thresholds derived from this level's definition. Used
     * only when [starThresholds] is absent. Real tuning happens later (E22 closed
     * test); this is a documented rough pass.
     *
     * Rule (the engine scores 10 points per cleared piece — see `WorldBoardExtensions.scoreDeleteAndCheckGoals`):
     *   goalPoints = sum(goal.target) * 10   // minimum score from collecting every goal piece
     *   base       = goalPoints, or a flat fallback when the level declares no goals
     *   s1 = base                              // a bare-minimum win just clears the goals
     *   s2 = base + base / 2                    // a comfortable win with cascades
     *   s3 = base * 2                            // an efficient win leaving moves / big cascades
     */
    private fun defaultStarThresholds(): List<Int> {
        val goalPoints = goals.sumOf { it.target } * 10
        val base = if (goalPoints > 0) goalPoints else DEFAULT_STAR_THRESHOLD_BASE
        return listOf(base, base + base / 2, base * 2)
    }

    private companion object {
        /** Flat fallback base when a level declares no collection goals. */
        const val DEFAULT_STAR_THRESHOLD_BASE = 300
    }
}

/**
 * Normalizes raw star-threshold input into a strictly-increasing 3-element ladder,
 * or returns `null` when no thresholds were supplied (so the caller can fall back to
 * level-derived defaults).
 *
 * Malformed input is **normalized**, not rejected:
 *   - values are sorted ascending;
 *   - ties / inversions are repaired by nudging each value to `prev + 1`;
 *   - the ladder is coerced to exactly three cutoffs — extras beyond three are
 *     dropped, and fewer than three are padded by stepping up from the last value.
 *
 * Examples:
 *   `null` / `[]`            -> `null`
 *   `[1000, 2000, 3000]`     -> `[1000, 2000, 3000]`
 *   `[3000, 1000, 2000]`     -> `[1000, 2000, 3000]`   (sorted)
 *   `[1000, 1000, 1000]`     -> `[1000, 1001, 1002]`   (strictness repaired)
 *   `[1500]`                 -> `[1500, 1501, 1502]`   (padded to 3)
 */
fun normalizeStarThresholds(raw: List<Int>?): List<Int>? {
    if (raw.isNullOrEmpty()) return null
    val sorted = raw.sorted().let { if (it.size > 3) it.take(3) else it }
    val first = sorted.first()
    val last = sorted.last()
    val step = (last - first).let { if (it > 0) it else 1 }
    val padded = sorted.toMutableList()
    while (padded.size < 3) padded.add(padded.last() + step)
    for (i in 1 until padded.size) {
        if (padded[i] <= padded[i - 1]) padded[i] = padded[i - 1] + 1
    }
    return padded
}
